import { ModalProps } from '@/types/shared'
import { useTranslation } from 'react-i18next'
import { Button, Dialog } from 'vnetwork-ui'
import {
    createColumns,
    DataTableCustomized,
} from '@/components/ui/data-table-customized'
import { getEmployeesByIds } from '@/modules/Employee/services'
import { Employee } from '@/modules/Employee/types/response'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMemo, useState } from 'react'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface PreviewSelectionModalProps extends ModalProps {
    selectedKeys: string[]
    onClearSelection?: () => void
}

const PreviewSelectionModal: React.FC<PreviewSelectionModalProps> = ({
    open,
    onClose,
    selectedKeys,
    onClearSelection,
}) => {
    const { t } = useTranslation()

    // Client-side pagination state
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)

    // Fetch selected employees
    const { data: employees = [], isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.EMPLOYEES, 'preview', selectedKeys.join(',')],
        queryFn: () =>
            getEmployeesByIds({
                employee_ids: selectedKeys.map((key) => Number(key)),
            }),
        enabled: open && selectedKeys.length > 0,
    })

    // Simplified columns for preview (only fields returned by contains API)
    const columns = useMemo(
        () =>
            createColumns<Employee>([
                {
                    key: 'employee_code',
                    title: t('employee_code'),
                    width: 100,
                },
                {
                    key: 'employee_name',
                    title: t('employee_name'),
                    width: 180,
                },
                {
                    key: 'department_name',
                    title: t('department_id'),
                    width: 150,
                },
                {
                    key: 'position_name',
                    title: t('position_id'),
                    width: 150,
                },
                {
                    key: 'employee_email_company',
                    title: t('employee_email_company'),
                    width: 200,
                    cellClassName: 'truncate',
                },
            ]),
        [t]
    )

    // Client-side pagination
    const paginatedEmployees = useMemo(() => {
        const startIndex = (page - 1) * pageSize
        return employees.slice(startIndex, startIndex + pageSize)
    }, [employees, page, pageSize])

    const handlePageChange = (newPage: number) => {
        setPage(newPage)
    }

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize)
        setPage(1) // Reset to first page
    }

    const handleClearAndClose = () => {
        onClearSelection?.()
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('preview_selection')}
            maxWidth="lg"
        >
            <div className="p-4 space-y-4">
                <div className="text-sm text-gray-600">
                    {t('selected_count', { count: selectedKeys.length })}
                </div>

                <DataTableCustomized<Employee>
                    columns={columns}
                    rows={paginatedEmployees}
                    rowKey={(row) => row.employee_id}
                    loading={isLoading}
                    size="small"
                    maxHeight="400px"
                    pinnedHeader
                    pagination={{
                        page,
                        pageSize,
                        total: employees.length,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                        pageSizeOptions: [10, 20, 50],
                    }}
                />

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button variant="outlined" onClick={onClose} type="button">
                        {t('close')}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleClearAndClose}
                        type="button"
                    >
                        {t('clear_selection')}
                    </Button>
                </div>
            </div>
        </Dialog>
    )
}

export default PreviewSelectionModal
